import React, { useState, useEffect } from 'react';
import {
  Webhook, Plus, Edit2, Trash2, Power, PowerOff,
  RotateCw, Eye, EyeOff, Copy, CheckCircle, XCircle, Clock,
  RefreshCw, X, KeyRound
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import LoadingSpinner from './LoadingSpinner';
import styles from './WebhookManager.module.css';

export default function WebhookManager() {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [showDeliveries, setShowDeliveries] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [revealedSecrets, setRevealedSecrets] = useState({});
  const [loadingSecret, setLoadingSecret] = useState(null);

  useEffect(() => {
    loadWebhooks();
  }, []);

  const loadWebhooks = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/v1/webhooks`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });

      if (!response.ok) throw new Error('Failed to load webhooks');

      const data = await response.json();
      setWebhooks(data.webhooks || []);
    } catch (err) {
      console.error('Error loading webhooks:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDeliveries = async (webhookId) => {
    try {
      setLoadingDeliveries(true);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/webhooks/${webhookId}/deliveries?limit=20`,
        { headers: { 'Authorization': `Bearer ${session.access_token}` } }
      );

      if (!response.ok) throw new Error('Failed to load deliveries');

      const data = await response.json();
      setDeliveries(data.deliveries || []);
    } catch (err) {
      console.error('Error loading deliveries:', err);
    } finally {
      setLoadingDeliveries(false);
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    if (!confirm('Are you sure you want to delete this webhook? This action cannot be undone.')) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/webhooks/${webhookId}`,
        { method: 'DELETE', headers: { 'Authorization': `Bearer ${session.access_token}` } }
      );

      if (!response.ok) throw new Error('Failed to delete webhook');

      setWebhooks(prev => prev.filter(w => w.id !== webhookId));
    } catch (err) {
      console.error('Error deleting webhook:', err);
      alert('Failed to delete webhook: ' + err.message);
    }
  };

  const handleToggleActive = async (webhook) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/webhooks/${webhook.id}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ active: !webhook.active })
        }
      );

      if (!response.ok) throw new Error('Failed to update webhook');

      const data = await response.json();
      setWebhooks(prev => prev.map(w => w.id === webhook.id ? { ...w, active: data.active } : w));
    } catch (err) {
      console.error('Error toggling webhook:', err);
      alert('Failed to update webhook: ' + err.message);
    }
  };

  const handleRevealSecret = async (webhookId) => {
    if (revealedSecrets[webhookId]) {
      setRevealedSecrets(prev => { const next = { ...prev }; delete next[webhookId]; return next; });
      return;
    }
    try {
      setLoadingSecret(webhookId);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/webhooks/${webhookId}`,
        { headers: { 'Authorization': `Bearer ${session.access_token}` } }
      );
      if (!response.ok) throw new Error('Failed to fetch webhook secret');
      const data = await response.json();
      setRevealedSecrets(prev => ({ ...prev, [webhookId]: data.webhook.secret }));
    } catch (err) {
      console.error('Error revealing secret:', err);
      alert('Failed to reveal webhook secret');
    } finally {
      setLoadingSecret(null);
    }
  };

  const handleViewDeliveries = (webhook) => {
    setShowDeliveries(webhook.id);
    loadDeliveries(webhook.id);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  if (loading) {
    return (
      <div className={styles.loadingWrap}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorBox}>
        <p>Error loading webhooks: {error}</p>
        <button onClick={loadWebhooks} className={styles.retryBtn}>Try again</button>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.headerTitle}>Webhook Management</h2>
          <p className={styles.headerSubtitle}>Manage webhook subscriptions for real-time event notifications</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className={styles.createBtn}>
          <Plus size={20} aria-hidden="true" />
          Create Webhook
        </button>
      </div>

      {webhooks.length === 0 ? (
        <div className={styles.emptyState}>
          <Webhook size={48} className={styles.emptyIcon} aria-hidden="true" />
          <h3 className={styles.emptyTitle}>No webhooks yet</h3>
          <p className={styles.emptyBody}>Create your first webhook to receive real-time event notifications</p>
          <button onClick={() => setShowCreateModal(true)} className={styles.createBtn}>
            <Plus size={20} aria-hidden="true" />
            Create Webhook
          </button>
        </div>
      ) : (
        <div className={styles.list}>
          {webhooks.map((webhook) => (
            <div key={webhook.id} className={styles.webhookCard}>
              <div className={styles.webhookCardInner}>
                <div className={styles.webhookLeft}>
                  <div className={styles.webhookStatusRow}>
                    {webhook.active ? (
                      <span className={`${styles.statusBadge} ${styles.statusBadgeActive}`}>
                        <Power size={14} aria-hidden="true" />
                        Active
                      </span>
                    ) : (
                      <span className={`${styles.statusBadge} ${styles.statusBadgeInactive}`}>
                        <PowerOff size={14} aria-hidden="true" />
                        Inactive
                      </span>
                    )}
                    {webhook.description && (
                      <span className={styles.webhookDesc}>{webhook.description}</span>
                    )}
                  </div>

                  <div className={styles.urlSection}>
                    <label className={styles.fieldLabel}>Webhook URL</label>
                    <div className={styles.urlRow}>
                      <code className={styles.urlCode}>{webhook.url}</code>
                      <button
                        onClick={() => copyToClipboard(webhook.url)}
                        className={styles.iconBtn}
                        title="Copy URL"
                      >
                        <Copy size={16} aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  <div className={styles.eventsSection}>
                    <label className={styles.fieldLabel}>Subscribed Events</label>
                    <div className={styles.eventTags}>
                      {webhook.events.map((event) => (
                        <span key={event} className={styles.eventTag}>{event}</span>
                      ))}
                    </div>
                  </div>

                  <div className={styles.secretSection}>
                    <label className={styles.fieldLabel}>
                      <KeyRound size={12} aria-hidden="true" />
                      Signing Secret
                    </label>
                    {revealedSecrets[webhook.id] ? (
                      <div className={styles.secretRow}>
                        <code className={styles.secretCode}>{revealedSecrets[webhook.id]}</code>
                        <button
                          onClick={() => copyToClipboard(revealedSecrets[webhook.id])}
                          className={styles.iconBtn}
                          title="Copy secret"
                        >
                          <Copy size={16} aria-hidden="true" />
                        </button>
                        <button
                          onClick={() => handleRevealSecret(webhook.id)}
                          className={styles.iconBtn}
                          title="Hide secret"
                        >
                          <EyeOff size={16} aria-hidden="true" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleRevealSecret(webhook.id)}
                        disabled={loadingSecret === webhook.id}
                        className={styles.revealBtn}
                      >
                        {loadingSecret === webhook.id ? (
                          <><RefreshCw size={14} className={styles.spinningIcon} aria-hidden="true" /> Loading...</>
                        ) : (
                          <><Eye size={14} aria-hidden="true" /> Reveal Secret</>
                        )}
                      </button>
                    )}
                  </div>

                  <div className={styles.webhookMeta}>
                    <span>Created {new Date(webhook.created_at).toLocaleDateString()}</span>
                    {webhook.updated_at && webhook.updated_at !== webhook.created_at && (
                      <span>Updated {new Date(webhook.updated_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>

                <div className={styles.webhookActions}>
                  <button
                    onClick={() => handleViewDeliveries(webhook)}
                    className={styles.iconBtn}
                    title="View delivery history"
                  >
                    <Eye size={18} aria-hidden="true" />
                  </button>
                  <button
                    onClick={() => handleToggleActive(webhook)}
                    className={styles.iconBtn}
                    title={webhook.active ? 'Deactivate' : 'Activate'}
                  >
                    {webhook.active ? <PowerOff size={18} aria-hidden="true" /> : <Power size={18} aria-hidden="true" />}
                  </button>
                  <button
                    onClick={() => { setSelectedWebhook(webhook); setShowCreateModal(true); }}
                    className={styles.iconBtn}
                    title="Edit webhook"
                  >
                    <Edit2 size={18} aria-hidden="true" />
                  </button>
                  <button
                    onClick={() => handleDeleteWebhook(webhook.id)}
                    className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                    title="Delete webhook"
                  >
                    <Trash2 size={18} aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showDeliveries && (
        <DeliveryHistoryModal
          webhookId={showDeliveries}
          deliveries={deliveries}
          loading={loadingDeliveries}
          onClose={() => { setShowDeliveries(null); setDeliveries([]); }}
          onRefresh={() => loadDeliveries(showDeliveries)}
        />
      )}

      {showCreateModal && (
        <WebhookModal
          webhook={selectedWebhook}
          onClose={() => { setShowCreateModal(false); setSelectedWebhook(null); }}
          onSave={() => { setShowCreateModal(false); setSelectedWebhook(null); loadWebhooks(); }}
        />
      )}
    </div>
  );
}

// Delivery History Modal
function DeliveryHistoryModal({ webhookId, deliveries, loading, onClose, onRefresh }) {
  const statusIconClass = (status) => {
    switch (status) {
      case 'success':  return styles.iconSuccess;
      case 'failed':   return styles.iconError;
      case 'retrying': return styles.iconAmber;
      default:         return styles.iconMuted;
    }
  };

  const statusBadgeClass = (status) => {
    switch (status) {
      case 'success':  return styles.deliveryStatusSuccess;
      case 'failed':   return styles.deliveryStatusFailed;
      case 'retrying': return styles.deliveryStatusRetrying;
      default:         return styles.deliveryStatusPending;
    }
  };

  const DeliveryIcon = ({ status }) => {
    const cls = statusIconClass(status);
    switch (status) {
      case 'success':  return <CheckCircle size={16} className={cls} aria-hidden="true" />;
      case 'failed':   return <XCircle size={16} className={cls} aria-hidden="true" />;
      case 'retrying': return <RotateCw size={16} className={cls} aria-hidden="true" />;
      default:         return <Clock size={16} className={cls} aria-hidden="true" />;
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={`${styles.modal} ${styles.modalLg} ${styles.modalFlex}`}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Delivery History</h3>
          <div className={styles.modalHeaderActions}>
            <button onClick={onRefresh} className={styles.iconBtn} title="Refresh">
              <RefreshCw size={18} aria-hidden="true" />
            </button>
            <button onClick={onClose} className={styles.iconBtn} aria-label="Close">
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className={styles.modalFlexBody}>
          {loading ? (
            <div className={styles.loadingWrap}>
              <LoadingSpinner />
            </div>
          ) : deliveries.length === 0 ? (
            <div className={styles.emptyState}>
              <Clock size={48} className={styles.emptyIcon} aria-hidden="true" />
              <h4 className={styles.emptyTitle}>No deliveries yet</h4>
              <p className={styles.emptyBody}>Webhook deliveries will appear here once events are triggered</p>
            </div>
          ) : (
            <div className={styles.deliveryList}>
              {deliveries.map((delivery) => (
                <div key={delivery.id} className={styles.deliveryCard}>
                  <div className={styles.deliveryCardTop}>
                    <div className={styles.deliveryLeft}>
                      <DeliveryIcon status={delivery.status} />
                      <div>
                        <div className={styles.deliveryEventRow}>
                          <span className={styles.deliveryEventType}>{delivery.event_type}</span>
                          <span className={`${styles.deliveryStatusBadge} ${statusBadgeClass(delivery.status)}`}>
                            {delivery.status}
                          </span>
                        </div>
                        <div className={styles.deliveryTime}>
                          {new Date(delivery.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <div className={styles.deliveryRight}>
                      {delivery.response_status && (
                        <div className={delivery.response_status === 200 ? styles.httpSuccess : styles.httpError}>
                          HTTP {delivery.response_status}
                        </div>
                      )}
                      <div className={styles.deliveryAttempts}>Attempt {delivery.attempts}</div>
                    </div>
                  </div>

                  {delivery.error_message && (
                    <div className={styles.deliveryError}>
                      <strong>Error:</strong> {delivery.error_message}
                    </div>
                  )}
                  {delivery.next_retry_at && (
                    <div className={styles.deliveryNote}>
                      Next retry: {new Date(delivery.next_retry_at).toLocaleString()}
                    </div>
                  )}
                  {delivery.delivered_at && (
                    <div className={styles.deliveryNote}>
                      Delivered: {new Date(delivery.delivered_at).toLocaleString()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Webhook Create/Edit Modal
function WebhookModal({ webhook, onClose, onSave }) {
  const [url, setUrl] = useState(webhook?.url || '');
  const [description, setDescription] = useState(webhook?.description || '');
  const [selectedEvents, setSelectedEvents] = useState(webhook?.events || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const availableEvents = [
    'booking.confirmed',
    'consent.granted',
    'consent.revoked',
    'offer.created',
    'offer.accepted',
    'offer.rejected',
    'offer.expired',
    'availability.synced',
    'match.found'
  ];

  const toggleEvent = (event) => {
    setSelectedEvents(prev =>
      prev.includes(event) ? prev.filter(e => e !== event) : [...prev, event]
    );
  };

  const handleSave = async () => {
    if (!url.trim()) { setError('URL is required'); return; }
    if (selectedEvents.length === 0) { setError('Please select at least one event'); return; }

    try {
      setSaving(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const method = webhook ? 'PUT' : 'POST';
      const endpoint = webhook
        ? `${import.meta.env.VITE_API_BASE_URL}/v1/webhooks/${webhook.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/v1/webhooks`;

      const response = await fetch(endpoint, {
        method,
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim(), events: selectedEvents, description: description.trim() || null })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save webhook');
      }

      onSave();
    } catch (err) {
      console.error('Error saving webhook:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={`${styles.modal} ${styles.modalFlex}`}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>{webhook ? 'Edit Webhook' : 'Create Webhook'}</h3>
        </div>

        <div className={styles.modalFlexBody}>
          <div className={styles.webhookForm}>
            {error && (
              <div className={styles.errorBox}>
                <p>{error}</p>
              </div>
            )}

            <div className={styles.formField}>
              <label className={styles.formLabel}>Webhook URL *</label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://your-domain.com/webhook"
                className={styles.formInput}
              />
              <p className={styles.fieldHint}>The URL where webhook events will be sent</p>
            </div>

            <div className={styles.formField}>
              <label className={styles.formLabel}>Description (optional)</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., Production webhook for Zapier"
                className={styles.formInput}
              />
            </div>

            <div className={styles.formField}>
              <label className={styles.formLabel}>Events to Subscribe *</label>
              <div className={styles.eventsList}>
                {availableEvents.map((event) => (
                  <label key={event} className={styles.eventCheckLabel}>
                    <input
                      type="checkbox"
                      checked={selectedEvents.includes(event)}
                      onChange={() => toggleEvent(event)}
                      className={styles.eventCheckbox}
                    />
                    <span className={styles.eventCheckName}>{event}</span>
                  </label>
                ))}
              </div>
              <p className={styles.fieldHint}>Select which events will trigger this webhook</p>
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.cancelBtn}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className={styles.saveBtn}>
            {saving ? 'Saving...' : webhook ? 'Save Changes' : 'Create Webhook'}
          </button>
        </div>
      </div>
    </div>
  );
}
